import { useState } from 'react'
import { isAfter } from 'date-fns'
import { useSnackbar } from 'notistack'

import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import TournamentWebsocketsStatus from '@/components/DataAdmin/Tournaments/TournamentWebsocketsStatus'
import UpdateTournamentDataStatusForm from '@/components/forms/UpdateTournamentDataStatusForm'
import { useCooldownButton } from '@/hooks/useCooldownButton'
import { ddeApi } from '@/service/ddeApi'
import { GolfTournamentWithFeedCount } from '@/stores/tournaments/tournaments.utils'

type Props = {
  tournament?: GolfTournamentWithFeedCount | null
  tournamentId: number
  hasWebsockets: boolean
  fetchData: () => Promise<void>
}
const TournamentToolbar = ({ tournament, hasWebsockets, fetchData, tournamentId }: Props) => {
  const { enqueueSnackbar } = useSnackbar()
  const [showDialog, setShowDialog] = useState(false)
  const { cooldown, startCooldown } = useCooldownButton({
    key: 'data-admin-tournament-refresh-button'
  })

  const canUpdateStatus = () => {
    if (!tournament) return true

    const today = new Date()
    const tournamentEndDate = new Date(tournament.endDate)
    return isAfter(today, tournamentEndDate)
  }

  const refreshCache = async () => {
    try {
      startCooldown()
      await ddeApi.endpoints.golf.tournaments.refreshCache(tournamentId)
      enqueueSnackbar('Successfully refreshed cache', { variant: 'success' })
      await fetchData()
    } catch (e) {
      enqueueSnackbar('Unable to refresh cache', { variant: 'error' })
    }
  }

  const purgeCache = async () => {
    try {
      await ddeApi.endpoints.golf.tournaments.purgeCache(tournamentId)
      enqueueSnackbar('Successfully purged cache', { variant: 'success' })
      await fetchData()
    } catch (e) {
      enqueueSnackbar('Unable to purged cache', { variant: 'error' })
    }
  }

  return (
    <>
      {tournament && hasWebsockets && <TournamentWebsocketsStatus tournament={tournament} />}
      <Stack sx={{ marginLeft: 'auto' }} direction="row" spacing={1}>
        <UpdateTournamentDataStatusForm
          open={showDialog}
          tournamentId={tournamentId}
          tournament={tournament}
          onClose={() => setShowDialog(false)}
          onSubmit={fetchData}
        />
        <Button variant="outlined" onClick={() => setShowDialog(true)}>
          Update Tournament Data Status
        </Button>
        <Button variant="outlined" disabled={cooldown > 0} onClick={() => refreshCache()}>
          {cooldown > 0 ? `Refresh Cache (${cooldown}s)` : 'Refresh Cache'}
        </Button>
        <Tooltip
          title={!canUpdateStatus() && 'Can only purge cache after tournament end date'}
          placement="bottom"
          arrow
        >
          <span>
            <Button variant="outlined" onClick={purgeCache} disabled={!canUpdateStatus()}>
              Purge Cache
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </>
  )
}

export default TournamentToolbar
